import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = [
    "input",
    "dropzone",
    "progress",
    "progressBar",
    "progressText",
    "fileInfo",
    "fileName",
    "fileSize",
    "statusBadge",
    "contentArea",
    "hiddenToken",
    "hiddenContent",
    "sessionDate",
    "errorMessage",
    "errorText",
    "replaceButton",
    "removeButton"
  ]

  static values = {
    uploadUrl: String,
    createUrl: String,
    maxSize: { type: Number, default: 104857600 },
    acceptedTypes: { type: Array, default: ["audio/mpeg", "audio/mp4", "audio/x-m4a", "audio/mp3"] },
    token: String,
    pollingInterval: { type: Number, default: 2000 }
  }

  connect() {
    this.uploadInProgress = false
    this.transcriptionComplete = false
    this.subscription = null
    this.pollingTimer = null

    if (this.tokenValue) {
      this.subscribeToUpdates()
    }

    this.dispatchReadyState()
  }

  disconnect() {
    this.unsubscribe()
    this.stopPolling()
  }

  handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('border-indigo-500', 'bg-indigo-50')
    }
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    if (e.target === this.dropzoneTarget) {
      this.dropzoneTarget.classList.remove('border-indigo-500', 'bg-indigo-50')
    }
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('border-indigo-500', 'bg-indigo-50')
    }

    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.processFile(files[0])
    }
  }

  openFileDialog(e) {
    e.preventDefault()
    this.inputTarget.click()
  }

  handleFileSelect(e) {
    const file = e.target.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  processFile(file) {
    if (!this.acceptedTypesValue.includes(file.type)) {
      this.showError('Please select an MP3 or M4A audio file')
      return
    }

    if (file.size > this.maxSizeValue) {
      this.showError(`File size exceeds maximum of ${this.formatFileSize(this.maxSizeValue)}`)
      return
    }

    this.clearError()
    this.currentFile = file
    this.displayFileInfo(file)
    this.uploadAndTranscribe(file)
  }

  displayFileInfo(file) {
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = file.name
    if (this.hasFileSizeTarget) this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    if (this.hasFileInfoTarget) this.fileInfoTarget.classList.remove('hidden')
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.add('hidden')
    if (this.hasReplaceButtonTarget) this.replaceButtonTarget.classList.add('hidden')
  }

  uploadAndTranscribe(file) {
    this.uploadInProgress = true
    this.transcriptionComplete = false

    // Reset progress bar state
    if (this.hasProgressTarget) this.progressTarget.classList.remove('hidden')
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '0%'
      this.progressBarTarget.classList.remove('bg-green-600')
      this.progressBarTarget.classList.add('bg-indigo-600')
    }
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = 'Uploading... 0%'

    this.showTranscribingStatus()
    this.dispatchReadyState()

    const upload = new DirectUpload(file, this.uploadUrlValue, this)

    upload.create((error, blob) => {
      if (error) {
        this.showError(`Upload failed: ${error}`)
        this.resetUpload()
      } else {
        this.uploadComplete(blob)
      }
    })
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      const percentage = Math.round((event.loaded / event.total) * 100)
      if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${percentage}%`
      if (this.hasProgressTextTarget) this.progressTextTarget.textContent = `Uploading... ${percentage}%`
    })
  }

  uploadComplete(blob) {
    this.uploadInProgress = false
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove('bg-indigo-600')
      this.progressBarTarget.classList.add('bg-green-600')
    }
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = 'Upload complete! Starting transcription...'

    this.createTemporaryTranscription(blob.signed_id)
  }

  async createTemporaryTranscription(blobSignedId) {
    const sessionDate = this.hasSessionDateTarget ? this.sessionDateTarget.value : null
    const isReplacing = !!this.tokenValue

    try {
      const url = isReplacing
        ? `/psychologists/temporary_transcriptions/${this.tokenValue}/replace`
        : this.createUrlValue

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          blob_signed_id: blobSignedId,
          session_date: sessionDate
        })
      })

      const data = await response.json()

      if (data.success) {
        this.tokenValue = data.token
        if (this.hasHiddenTokenTarget) this.hiddenTokenTarget.value = data.token
        this.dispatchReadyState() // Dispatch with token now set
        this.subscribeToUpdates()
        this.startPolling()
      } else {
        this.showError(data.errors?.join(', ') || 'Failed to start transcription')
        this.resetUpload()
      }
    } catch (error) {
      this.showError(`Network error: ${error.message}`)
      this.resetUpload()
    }
  }

  subscribeToUpdates() {
    if (!this.tokenValue || this.subscription) return

    const streamName = `temporary_transcription_${this.tokenValue}`

    this.subscription = consumer.subscriptions.create(
      { channel: "Turbo::StreamsChannel", signed_stream_name: this.signedStreamName(streamName) },
      {
        received: (data) => {
          Turbo.renderStreamMessage(data)
          this.checkTranscriptionStatus()
        }
      }
    )
  }

  signedStreamName(name) {
    return name
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  startPolling() {
    this.stopPolling()
    this.pollingTimer = setInterval(() => this.pollStatus(), this.pollingIntervalValue)
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  async pollStatus() {
    if (!this.tokenValue) return

    try {
      const response = await fetch(`/psychologists/temporary_transcriptions/${this.tokenValue}`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        this.updateStatusFromData(data)
      }
    } catch (error) {
      console.error('[TranscriptionEditor] Polling error:', error)
    }
  }

  updateStatusFromData(data) {
    if (data.status === 'completed') {
      this.stopPolling()
      this.transcriptionComplete = true
      this.showCompletedStatus()
      this.updateContentArea(data.content)
      if (this.hasReplaceButtonTarget) this.replaceButtonTarget.classList.remove('hidden')
      this.dispatchReadyState()
    } else if (data.status === 'failed') {
      this.stopPolling()
      this.transcriptionComplete = false
      this.showFailedStatus(data.error)
      this.dispatchReadyState()
    }
  }

  checkTranscriptionStatus() {
    this.pollStatus()
  }

  showTranscribingStatus() {
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <svg class="w-3 h-3 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Transcribing...
        </span>
      `
      this.statusBadgeTarget.classList.remove('hidden')
    }

    if (this.hasContentAreaTarget) {
      this.contentAreaTarget.innerHTML = `
        <div class="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div class="flex items-center justify-center">
            <svg class="w-5 h-5 mr-2 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-sm text-gray-600">Processing transcription... This may take a few minutes.</span>
          </div>
        </div>
      `
      this.contentAreaTarget.classList.remove('hidden')
    }
  }

  showCompletedStatus() {
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <svg class="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          Successfully transcribed
        </span>
      `
    }

    if (this.hasProgressTarget) this.progressTarget.classList.add('hidden')
  }

  showFailedStatus(error) {
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <svg class="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          Failed
        </span>
      `
    }

    if (this.hasContentAreaTarget) {
      this.contentAreaTarget.innerHTML = `
        <div class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-700">${error || 'Transcription failed. Please try again.'}</p>
          <button type="button"
                  data-action="click->transcription-editor#retryTranscription"
                  class="mt-2 text-sm text-red-600 hover:text-red-800 font-medium">
            Retry Transcription
          </button>
        </div>
      `
    }
  }

  updateContentArea(content) {
    if (!this.hasContentAreaTarget) return

    const inputId = `temp_transcript_${this.tokenValue}_content`

    this.contentAreaTarget.innerHTML = `
      <div class="mt-3">
        <label class="block text-sm font-medium text-gray-700 mb-1">Transcription</label>
        <div class="border border-gray-300 rounded-lg shadow-sm overflow-hidden bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
          <input type="hidden"
                 id="${inputId}"
                 name="transcripts[0][content]"
                 value="${this.escapeHtml(content || '')}">
          <trix-editor
            input="${inputId}"
            class="trix-content prose prose-sm max-w-none p-3 min-h-[150px] focus:outline-none"
            data-transcription-editor-target="editor"
            data-action="trix-change->transcription-editor#contentChanged"></trix-editor>
        </div>
        <p class="mt-1 text-xs text-gray-500">You can edit the transcription before sending the invitation.</p>
      </div>
    `

    if (this.hasHiddenContentTarget) {
      this.hiddenContentTarget.value = content || ''
    }

    const trixEditor = this.contentAreaTarget.querySelector('trix-editor')
    if (trixEditor) {
      setTimeout(() => {
        if (trixEditor.editor) {
          trixEditor.editor.loadHTML(content || '')
        }
      }, 100)
    }
  }

  contentChanged(e) {
    if (this.hasHiddenContentTarget) {
      this.hiddenContentTarget.value = e.target.value
    }
  }

  async retryTranscription() {
    if (!this.tokenValue || !this.currentFile) return

    this.uploadAndTranscribe(this.currentFile)
  }

  replaceFile(e) {
    e.preventDefault()
    // Clear the input value so change event fires even if selecting the same file
    this.inputTarget.value = ''
    this.inputTarget.click()
  }

  async removeFile(e) {
    e.preventDefault()

    const oldToken = this.tokenValue

    if (oldToken) {
      try {
        await fetch(`/psychologists/temporary_transcriptions/${oldToken}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          }
        })
      } catch (error) {
        console.error('[TranscriptionEditor] Delete error:', error)
      }
    }

    this.resetUpload()
    this.tokenValue = ''
    if (this.hasHiddenTokenTarget) this.hiddenTokenTarget.value = ''
    this.transcriptionComplete = false

    // Dispatch to notify parent controller with the old token for cleanup
    this.dispatch('readyStateChanged', {
      detail: {
        ready: false,
        token: oldToken || '',
        uploadInProgress: false,
        wasRemoved: true
      },
      bubbles: true
    })
  }

  resetUpload() {
    this.uploadInProgress = false
    this.currentFile = null
    this.stopPolling()
    this.unsubscribe()

    if (this.hasFileInfoTarget) this.fileInfoTarget.classList.add('hidden')
    if (this.hasProgressTarget) this.progressTarget.classList.add('hidden')
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove('hidden')
    if (this.hasStatusBadgeTarget) this.statusBadgeTarget.classList.add('hidden')
    if (this.hasContentAreaTarget) {
      this.contentAreaTarget.innerHTML = ''
      this.contentAreaTarget.classList.add('hidden')
    }
    if (this.hasReplaceButtonTarget) this.replaceButtonTarget.classList.add('hidden')

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '0%'
      this.progressBarTarget.classList.remove('bg-green-600')
      this.progressBarTarget.classList.add('bg-indigo-600')
    }
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = 'Uploading... 0%'
    if (this.hasInputTarget) this.inputTarget.value = ''

    this.clearError()
  }

  showError(message) {
    if (this.hasErrorTextTarget) this.errorTextTarget.textContent = message
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.remove('hidden')
  }

  clearError() {
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.add('hidden')
  }

  dispatchReadyState() {
    const isReady = this.transcriptionComplete && !this.uploadInProgress
    this.dispatch('readyStateChanged', {
      detail: {
        ready: isReady,
        token: this.tokenValue,
        uploadInProgress: this.uploadInProgress,
        transcriptionComplete: this.transcriptionComplete
      },
      bubbles: true
    })
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
